// /call/:id — the direct-call surface (Layer B). Calls NEVER touch the meeting
// route: this page is call-shaped end to end (loading, error, retry, leave), so
// no failure can strand anyone in a meeting lobby.
//
// The page is IDEMPOTENT: on every mount (or retry) it asks the server for its
// own grant via POST /v1/calls/{id}/join — the §5.5 reconnect analog. There is
// no client-side single-use grant handoff to lose: a refresh, a discarded React
// render, or a flaky first connect can always recover by asking again. Join
// never decides an outcome, so retrying is always safe.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { api, ApiError } from '../api';
import { AudioCallView } from '../components/AudioCallView';
import { BrizinaLoader } from '../components/BrizinaLoader';
import { othersOn, peerSummary } from '../lib/calls';
import { connectRoom } from '../lib/connectRoom';
import { humanConnectError } from '../lib/connectError';
import { engine } from '../engine';
import { Button } from '../ui/Button';
import { useCallStore } from '../store/callStore';
import { callsStore, clearCallEnded, useCallsStore } from '../store/callsStore';

type Phase =
  | { kind: 'connecting' }
  | {
      kind: 'in-call';
      slug: string;
      peerName: string;
      /** The other party's photo, for the 1:1 screen before they answer. */
      peerAvatar?: string;
      /** Rung but not answered yet — the stage shows them waiting. */
      invited: { id: string; name: string; avatar?: string }[];
      isCaller: boolean;
    }
  | { kind: 'error'; message: string };

export function CallPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>({ kind: 'connecting' });
  const connectingRef = useRef(false);
  // Leaving must run exactly once, whichever signal fires first (hang-up,
  // remote end, room drop).
  const leftRef = useRef(false);

  const leave = useCallback(
    (terminate: boolean) => {
      if (leftRef.current) return;
      leftRef.current = true;
      if (terminate) {
        // Answered → completed; never answered → canceled. The distinction is
        // what makes history honest ("2m 14s" vs "Canceled").
        const answered =
          callsStore.getState().active?.id === id ||
          engine.snapshots().some((a) => !a.isLocal);
        // If cancel loses the race with an accept (409), end the now-active
        // call instead — the peer must never be left in a half-open room.
        void (answered ? api.endCall(id) : api.cancelCall(id).catch(() => api.endCall(id))).catch(
          () => undefined,
        );
      }
      clearCallEnded();
      // REPLACE the history entry: the back button must never resurrect a dead
      // call (that was the "back lands in a waiting room" bug).
      void engine.disconnect().then(() => navigate('/calls', { replace: true }));
    },
    [id, navigate],
  );

  const connect = useCallback(() => {
    if (connectingRef.current) return;
    connectingRef.current = true;
    setPhase({ kind: 'connecting' });
    void (async () => {
      try {
        const r = await api.joinCall(id);
        await connectRoom(r.grant, { micEnabled: true, camEnabled: false });
        const isCaller = r.grant.identity === `u_${r.call.callerId}`;
        const me = r.grant.identity.replace(/^u_/, '');
        setPhase({
          kind: 'in-call',
          slug: r.call.slug,
          // Who this call is WITH: the other party, or — in a group — everyone
          // else on it, whichever side we are.
          peerName: peerSummary(othersOn(r.call, me)),
          peerAvatar: isCaller ? r.call.peers[0]?.avatar : r.call.callerAvatar,
          invited: r.call.peers
            .filter((p) => p.state === 'ringing' && p.id !== me)
            .map((p) => ({ id: p.id, name: p.name, avatar: p.avatar })),
          isCaller,
        });
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
          // The call is over (or was never ours) — nothing to retry.
          navigate('/calls', { replace: true });
          return;
        }
        setPhase({
          kind: 'error',
          message: humanConnectError(err),
        });
      } finally {
        connectingRef.current = false;
      }
    })();
  }, [id, navigate]);

  useEffect(() => {
    connect();
  }, [connect]);

  // Remote resolution (declined / no answer / canceled / peer ended): show the
  // reason for a beat, then leave. Nobody sits alone in a room that is over.
  const endedReason = useCallsStore((s) => (s.ended?.callID === id ? s.ended.reason : null));
  useEffect(() => {
    if (!endedReason) return;
    const t = window.setTimeout(() => leave(false), 1600);
    return () => window.clearTimeout(t);
  }, [endedReason, leave]);

  // The room dropped out from under us (network died, server ended it).
  const roomState = useCallStore((s) => s.roomState);
  useEffect(() => {
    if (phase.kind === 'in-call' && roomState === 'disconnected' && !endedReason) {
      leave(true);
    }
  }, [phase.kind, roomState, endedReason, leave]);

  const hangUp = useCallback(() => leave(true), [leave]);
  const retry = useCallback(() => connect(), [connect]);
  const back = useCallback(() => navigate('/calls', { replace: true }), [navigate]);

  switch (phase.kind) {
    case 'connecting':
      return (
        <div className="bz-callpage-state">
          <BrizinaLoader />
          <p className="bz-callpage-note">Connecting your call…</p>
        </div>
      );
    case 'error':
      return (
        <div className="bz-callpage-state">
          <p className="bz-callpage-note bz-error-text">{phase.message}</p>
          <div className="bz-callpage-actions">
            <Button variant="primary" onClick={retry}>
              Try again
            </Button>
            <Button onClick={back}>Back to calls</Button>
          </div>
        </div>
      );
    case 'in-call':
      return (
        <AudioCallView
          callId={id}
          slug={phase.slug}
          peerName={phase.peerName}
          peerAvatar={phase.peerAvatar}
          invited={phase.invited}
          isCaller={phase.isCaller}
          endedReason={endedReason}
          onHangUp={hangUp}
        />
      );
  }
}
